'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { del, post, put } from '@nineeng/request';
import AddIcon from '@mui/icons-material/Add';
import MuiButton from '@mui/material/Button';
import { Mail, MoreVertical, Pencil, Phone, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { CopyToClipboardIcon, DataGrid, DataGridColumn, FormDialog, Button } from '@repo/components';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@repo/components';
import { GroupContact, PERMISSIONS } from '@repo/shared/domain';
import { GroupContactForm } from '@repo/forms';
import { EventEmitter } from '@repo/shared/helpers';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroup } from 'src/api/admin/groups';

export const Route = createFileRoute('/_admin/groups/$groupId/contact-details')({
	loader: async ({ params }) => {
		const group = await getGroup(params.groupId);
		return { group };
	},
	component: ContactDetailsPage,
});

function ContactDetailsPage() {
	const { groupId } = Route.useParams();
	const queryClient = useQueryClient();
	const { group: initialGroup } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const [isGroupContactDialogOpen, setIsGroupContactDialogOpen] = useState(false);
	const [isDeleteGroupContactDialogOpen, setIsDeleteGroupContactDialogOpen] = useState(false);
	const [contactIdToDelete, setContactIdToDelete] = useState<string | null>(null);
	const [groupContactData, setGroupContactData] = useState<GroupContact | null>(null);

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const canEdit = verifyPermission([PERMISSIONS.adminGroupsEdit]);

	const { data: group, refetch: refetchGroup } = useQuery({
		queryKey: ['group', groupId],
		queryFn: () => getGroup(groupId),
		initialData: initialGroup,
	});

	const revalidateGroupDetail = () => {
		refetchGroup();
		queryClient.invalidateQueries({ queryKey: ['group', groupId] });
	};

	const handleOpenGroupContactDialog = (groupContact?: GroupContact) => {
		if (groupContact) {
			setGroupContactData({ ...groupContact });
		}
		setIsGroupContactDialogOpen(true);
	};

	const handleCloseGroupContactDialog = () => {
		setIsGroupContactDialogOpen(false);
		setGroupContactData({});
	};

	const addGroupContact = async (formData) => {
		try {
			await post(`/api/groups/${groupId}/contact`, formData);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully added company contact.',
			});
			setIsGroupContactDialogOpen(false);
			setGroupContactData(null);
			revalidateGroupDetail();
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to add company contact.',
			});
		}
	};

	const updateGroupContact = async (formData, contactId) => {
		try {
			await put(`/api/groups/${groupId}/contact/${contactId}`, formData);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully updated company contact.',
			});
			setIsGroupContactDialogOpen(false);
			setGroupContactData(null);
			revalidateGroupDetail();
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to update company contact.',
			});
		}
	};

	const handleOpenDeleteGroupContactDialog = (id: string) => {
		setContactIdToDelete(id);
		setIsDeleteGroupContactDialogOpen(true);
	};

	const handleCloseDeleteGroupContactDialog = () => {
		setContactIdToDelete(null);
		setIsDeleteGroupContactDialogOpen(false);
	};

	const deleteGroupContact = async () => {
		try {
			await del(`/api/groups/${groupId}/contact/${contactIdToDelete}`);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully deleted company contact.',
			});
			setIsDeleteGroupContactDialogOpen(false);
			setContactIdToDelete(null);
			revalidateGroupDetail();
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to delete company contact.',
			});
		}
	};

	const actionsMenu = (id: string, rowData: GroupContact) => {
		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="sm" className="flex h-6 w-6 p-0 data-[state=open]:bg-muted">
						<MoreVertical className="h-3 w-3" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-[160px]">
					<DropdownMenuItem key={'edit-contact'} onClick={() => handleOpenGroupContactDialog(rowData)}>
						<Pencil />
						Edit contact information
					</DropdownMenuItem>
					<DropdownMenuItem key={'delete-contact'} onClick={() => handleOpenDeleteGroupContactDialog(id)}>
						<Trash2 />
						Delete contact information
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	const columns: DataGridColumn<GroupContact>[] = [
		{
			headerName: 'Type',
			field: 'type',
			flex: 1,
		},
		{
			headerName: 'Name',
			field: 'name',
			width: 120,
		},
		{
			field: 'email',
			headerName: '',
			width: 10,
			renderCell: (c) => c.value && <CopyToClipboardIcon Icon={Mail} value={c.value} side={'top'} align={'center'} />,
			sortable: false,
		},
		{
			field: 'phone',
			headerName: '',
			width: 10,
			renderCell: (c) => c.value && <CopyToClipboardIcon Icon={Phone} value={c.value} side={'top'} align={'center'} />,
			sortable: false,
		},
		canEdit && {
			field: 'id',
			headerName: '',
			width: 10,
			renderCell: (c) => actionsMenu(c.value, c.row),
			sortable: false,
		},
	].filter(Boolean) as DataGridColumn<GroupContact>[];

	if (!group) {
		return null;
	}

	return (
		<>
			<div className="flex">
				<div className="flex-grow gap-1 items-center p-1 " />
				<MuiButton
					disabled={!canEdit}
					onClick={() => handleOpenGroupContactDialog()}
					variant="contained"
					size="small"
					color="secondary"
					startIcon={<AddIcon />}
					sx={{ marginRight: 2, marginY: 1 }}
				>
					Add Contact
				</MuiButton>
			</div>
			<div className="border-t border-gray-300 w-full">
				<DataGrid
					data={group?.contacts || []}
					columns={columns}
					paginationMode="server"
					sortingMode="server"
					sortingOrder={['asc', 'desc']}
					pagination
					rowCount={(group.contacts || []).length}
					onReload={revalidateGroupDetail}
				/>
			</div>
			{isGroupContactDialogOpen && (
				<GroupContactForm
					isOpen={isGroupContactDialogOpen}
					onClose={handleCloseGroupContactDialog}
					onAdd={addGroupContact}
					onUpdate={updateGroupContact}
					data={groupContactData}
				/>
			)}

			{isDeleteGroupContactDialogOpen && (
				<FormDialog
					isOpen={isDeleteGroupContactDialogOpen}
					onClose={handleCloseDeleteGroupContactDialog}
					title="Delete contact information"
					description="Are you sure you want to delete this contact information?"
					buttons={
						<>
							<Button variant="outline" className="bg-transparent" onClick={handleCloseDeleteGroupContactDialog}>
								Cancel
							</Button>
							<Button variant="destructive" onClick={deleteGroupContact}>
								Delete contact
							</Button>
						</>
					}
				></FormDialog>
			)}
		</>
	);
}
